// Topbar — «пульт» оболочки. Desktop (≥ lg): h-[62px], утопленный inset-поиск
// + аккуратные icon-кнопки, которые вдавливаются. Mobile: h-14, [≡] + бренд.
// Дизайн-язык «Картотека» §3 / §8.A · эталон design-lab/home-8-synthesis.html.

import { Button } from "@studyhub/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@studyhub/ui/components/tooltip";
import { IconBell, IconMenu2, IconMoon, IconSearch, IconSun } from "@tabler/icons-react";
import { Link, useLocation } from "@tanstack/react-router";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProfileDropdown } from "@/components/profile-menu/profile-dropdown";
import { RecordingIndicator } from "@/components/recordings/recording-indicator";
import { TutorPanelTrigger } from "@/components/tutor/tutor-panel-trigger";
import { cn } from "@/lib/cn";
import { trackAuthCta } from "@/lib/ecosystem-analytics";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { useCommandPaletteStore } from "@/stores/command-palette";
import { useLoginModalStore } from "@/stores/login-modal";
import { isWorkspaceRoute } from "./_layout-state";
import { DailyLoadPill } from "./daily-load-pill";
import { LocaleSwitcher } from "./locale-switcher";

interface TopbarProps {
	sidebarWidth: number;
	sidePanelWidth: number;
	isDesktop: boolean;
	isAuthenticated: boolean;
	onOpenMobileDrawer: () => void;
	onToggleTheme: () => void;
	resolvedTheme: "light" | "dark";
	onToggleNotifications: () => void;
	unreadNotifications?: number;
}

/**
 * Пункт гостевого меню в шапке.
 *
 * Пункт ведёт ЛИБО на отдельную страницу (`to`), ЛИБО на секцию лендинга
 * (`hash`) — третьего не дано, поэтому тип разведён объединением.
 *
 * `covers` есть у ОБОИХ видов и отвечает только за подсветку: это перечень
 * секций лендинга, которые пункт собой представляет. Секций на главной десять,
 * пунктов в шапке пять — без `covers` подсветка гасла бы на четырёх секциях
 * подряд («Маршрут темы», «Репетитор», «Персонажи», «Ритм»), и меню выглядело
 * бы сломанным. У страничного «Метода» `covers` тоже есть: на главной он
 * обязан подсвечиваться на одноимённой секции, хотя ведёт на `/method`.
 */
type GuestNavItem = {
	id: string;
	labelKey: string;
	covers: readonly string[];
} & ({ to: "/method"; hash?: never } | { hash: string; to?: never });

// Порядок `covers` внутри массива обязан совпадать с порядком секций в DOM —
// на нём стоит выбор «первая видимая сверху» (см. useScrollSpy).
//
// Тип объявлен АННОТАЦИЕЙ, а не `satisfies`: с `as const satisfies` каждый
// элемент сохраняет свой узкий литеральный тип без «второй половины» union'а,
// и обращение `item.to` внутри `.map()` перестаёт компилироваться на якорных
// пунктах. Аннотация приводит все элементы к одному union'у с `?: never`.
const GUEST_NAV_ITEMS: readonly GuestNavItem[] = [
	// «Метод» — единственный пункт-страница: у метода есть полноценный разбор со
	// ссылками на работы (`/method`), и вести на короткую секцию лендинга вместо
	// него значило бы прятать самую содержательную публичную страницу.
	{ id: "method", to: "/method", labelKey: "nav.method", covers: ["method"] },
	{ id: "course", hash: "course", labelKey: "nav.course", covers: ["course", "topic-route"] },
	{
		id: "toolbox",
		hash: "toolbox",
		labelKey: "nav.features",
		covers: ["tutor", "characters", "rhythm", "toolbox"],
	},
	{ id: "pricing", hash: "pricing", labelKey: "nav.pricing", covers: ["audiences", "pricing"] },
	{ id: "faq", hash: "faq", labelKey: "nav.faq", covers: ["faq"] },
];

/** Плоский список секций в порядке DOM: id секции → id пункта меню. */
const SPY_SECTIONS: readonly { sectionId: string; anchorId: string }[] = GUEST_NAV_ITEMS.flatMap(
	(item) => item.covers.map((sectionId) => ({ sectionId, anchorId: item.id })),
);

/**
 * Scroll-spy для якорной навигации лендинга: подсвечивает пункт меню,
 * чья секция сейчас в верхней зоне вьюпорта. rootMargin сдвигает «активную
 * зону» под шапку (top) и до нижней трети (bottom), чтобы переключение
 * происходило когда заголовок секции доходит до верха.
 */
function useScrollSpy(sections: readonly { sectionId: string; anchorId: string }[]): string | null {
	const [activeId, setActiveId] = useState<string | null>(null);

	useEffect(() => {
		const elements = sections
			.map((s) => document.getElementById(s.sectionId))
			.filter((el): el is HTMLElement => el !== null);
		if (elements.length === 0) return;

		const visible = new Set<string>();
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) visible.add(entry.target.id);
					else visible.delete(entry.target.id);
				}
				// Из видимых секций — первая по порядку в DOM (ближайшая к верху).
				// Ни одной в активной зоне (первый экран, футер) — гасим подсветку:
				// иначе на самом верху страницы горит «FAQ», а в футере «Тарифы» —
				// последнее увиденное значение, к тому, что на экране, отношения не
				// имеющее. Залипшая подсветка хуже отсутствующей.
				const firstVisible = sections.find((s) => visible.has(s.sectionId));
				setActiveId(firstVisible ? firstVisible.anchorId : null);
			},
			{ rootMargin: "-72px 0px -55% 0px", threshold: [0, 0.1, 0.5, 1] },
		);
		for (const element of elements) observer.observe(element);
		return () => observer.disconnect();
	}, [sections]);

	return activeId;
}

// Тактильная icon-кнопка «Картотека»: в покое прозрачная, на hover —
// мягкий well (`bg-muted`), без рамки/карточки — тот же язык, что у
// DailyLoadPill / ghost-кнопок. На :active слегка вдавливается.
const ICON_KEY_CLASS = cn(
	"size-9 cursor-pointer rounded-[9px] bg-transparent text-foreground",
	"transition-[background-color,transform] duration-fast",
	"hover:bg-muted active:translate-y-px",
);

export const Topbar = forwardRef<HTMLElement, TopbarProps>(function Topbar(
	{
		sidebarWidth,
		sidePanelWidth,
		isDesktop,
		isAuthenticated,
		onOpenMobileDrawer,
		onToggleTheme,
		resolvedTheme,
		onToggleNotifications,
		unreadNotifications = 0,
	},
	ref,
) {
	const { t } = useTranslation("layout");
	const openCmdK = useCommandPaletteStore((s) => s.open);
	// В воркспейсе курса свою «дневную норму» рисует шапка курса — два одинаковых
	// «Сегодня 18/30» на одном экране читаются как рассинхрон. Триггер репетитора
	// при этом больше НЕ гейтим: с этапа 1 плана единого репетитора глобальная
	// панель работает и в курсе (подавление в `__root.tsx` снято).
	// Сам топбар в воркспейсе не монтируется (`main-layout.tsx`) — гейт остаётся
	// страховкой на случай возврата шапки (этап 5, единая шапка).
	const isWorkspace = useLocation({ select: (l) => isWorkspaceRoute(l.pathname) });
	return (
		<header
			ref={ref}
			// Отдельный слой перехода. Без своего имени шапка попадает в снимок
			// содержимого, а каркас переключается РАНЬШЕ снимка (роутер коммитит
			// маршрут до вызова startViewTransition) — из-за этого в кадре
			// оказывались то список без шапки, то две шапки сразу. Именованный
			// элемент вынимается из снимка корня и живёт своим слоем; анимация
			// ему запрещена в styles.css, поэтому он просто есть или его нет.
			className={cn(
				"fixed top-0 right-0 z-(--sh-z-chrome) border-b border-border",
				// Плотная поверхность `bg-card` — та же, что у шапки воркспейса курса,
				// полосы темы и мобильной полосы вызова. До унификации топбар был
				// полупрозрачной подложкой с blur, и на границе «список → курс»
				// шапка меняла материал: одна «парила», вторая была плотной.
				//
				// Осознанная потеря: просвечивания контента под шапкой больше нет.
				// Это плата за единство поверхности (план §4.8), а не недосмотр.
				// В тёмной теме `--card` — тёмная поверхность, так что «белая шапка»
				// означает единый токен, а не залитый белый цвет.
				"bg-card",
				"transition-[left,right] duration-base ease-out",
				// Высота = контент + safe-area-top (notch/статус-бар в Capacitor с
				// viewport-fit=cover). pt сдвигает контент под notch. На обычном
				// web env()=0 → как было. --topbar-height в MainLayout совпадает.
				// Desktop тоже с inset: на iPad landscape `lg+` иначе шапка лезет
				// под status bar.
				isDesktop
					? "h-[calc(62px+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)]"
					: "left-0 h-[calc(3.5rem+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)]",
			)}
			style={
				isDesktop
					? { left: sidebarWidth, right: sidePanelWidth, viewTransitionName: "sh-topbar" }
					: { viewTransitionName: "sh-topbar" }
			}
		>
			{isDesktop ? (
				<div className="h-full flex min-w-0 items-center gap-3 px-6">
					{isAuthenticated ? (
						<>
							{/* Утопленный inset-поиск — открывает командную палитру.
							    min-w-0: при узком центре (открыт репетитор) сжимается, а не
							    держит 300px и не выталкивает «Сегодня»/кнопки за край. */}
							<button
								type="button"
								onClick={openCmdK}
								className={cn(
									"sh-inset-field group/search flex min-w-0 items-center gap-2.5 h-[38px] px-3",
									"max-w-[440px] flex-1 cursor-pointer rounded-[10px]",
									"border border-border text-muted-foreground text-[13px]",
									"transition-[box-shadow,border-color] duration-fast",
									"hover:border-border/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
								)}
								aria-label={t("topbar.searchOpen")}
							>
								<IconSearch size={17} stroke={1.75} className="shrink-0" />
								<span className="min-w-0 flex-1 truncate text-left">
									{t("topbar.searchPlaceholder")}
								</span>
								<kbd
									className={cn(
										"ml-auto hidden items-center gap-0.5 px-1.5 h-5 rounded-[5px]",
										"border border-border-strong bg-card text-2xs font-mono text-muted-foreground",
										"shadow-[0_1px_0_var(--line-2)]",
										// Хоткей прячем раньше текста — выигрываем ~36px под сжатие.
										"min-[1280px]:inline-flex",
									)}
								>
									⌘K
								</kbd>
							</button>

							{/* Оптическое выравнивание: репетитор (залитая плитка) и аватар
							    (залитый кружок) не имеют воздуха по краям, а луна/колокол —
							    тонкие глифы с ~9px воздуха в боксе. Поэтому крайним залитым
							    элементам добавляем mr/ml, чтобы визуальный зазор между ВСЕМИ
							    кнопками был одинаковым (иначе луна↔колокол кажется шире). */}
							<div className="ml-auto flex shrink-0 items-center gap-1.5">
								{/* Дневная норма — сквозной виджет всех разделов (§9). В воркспейсе
								    скрыт: там ту же метрику рисует шапка курса, а два одинаковых
								    «Сегодня 18/30» в одном экране читаются как рассинхрон. */}
								{!isWorkspace && <DailyLoadPill className="mr-1" />}
								<RecordingIndicator />
								<TutorButton className="mr-2.5" />
								<ThemeToggleButton resolvedTheme={resolvedTheme} onToggle={onToggleTheme} />
								<NotificationButton unread={unreadNotifications} onClick={onToggleNotifications} />
								<TopbarAvatar size={8} />
							</div>
						</>
					) : (
						<>
							<GuestNavLinks />
							<div className="flex shrink-0 items-center gap-1.5 ml-auto">
								<LocaleSwitcher />
								<ThemeToggleButton resolvedTheme={resolvedTheme} onToggle={onToggleTheme} />
								<Button
									size="sm"
									className="cursor-pointer"
									onClick={() => {
										trackAuthCta("studyhub_header_login");
										useLoginModalStore.getState().open();
									}}
								>
									{t("topbar.login")}
								</Button>
							</div>
						</>
					)}
				</div>
			) : (
				<div className="h-full flex items-center px-2">
					{/* Бургер — отделён вертикальной линией-разделителем справа
					    (структурный акцент, паттерн экосистемы ЦМО / CharacterHub):
					    кнопка крупнее и заметнее, чем «теряющаяся» иконка в ряду. */}
					<div className="flex h-full items-center border-r border-border-strong pr-2">
						<button
							type="button"
							onClick={onOpenMobileDrawer}
							className={cn(
								ICON_KEY_CLASS,
								"size-10 inline-flex items-center justify-center pointer-coarse:size-11",
							)}
							aria-label={t("topbar.openMenu")}
						>
							<IconMenu2 size={22} stroke={1.75} />
						</button>
					</div>
					{/* Логотип — только текст (без иконки сервиса, как «IEC Hero»
					    в CharacterHub). Клик → главная. */}
					<Link
						to="/"
						className="ml-3 inline-flex min-w-0 items-center cursor-pointer"
						aria-label={t("topbar.home")}
					>
						<span className="truncate text-lg font-semibold tracking-tight text-foreground">
							Study<span className="text-primary">Hub</span>
						</span>
					</Link>
					<div className="ml-auto flex h-full items-center gap-0.5 shrink-0">
						{isAuthenticated ? (
							<>
								{/* Mobile: кольцо минут скрыто — на узкой шапке оно
								    обрезает логотип «StudyHub» до «St…». */}
								<RecordingIndicator />
								{/* Mobile: с подписью «Репетитор». В курсе кнопка — в call-bar. */}
								<TutorButton className="h-10 px-2.5 pointer-coarse:h-11" />
								<ThemeToggleButton
									resolvedTheme={resolvedTheme}
									onToggle={onToggleTheme}
									className="size-10 pointer-coarse:size-11"
								/>
								{/* Уведомления — отделены вертикальной линией слева,
								    симметрично бургеру (структурный «обрамляющий» вид).
								    Профиль-аватар убран из шапки в drawer (footer): в
								    учебном сервисе он не нужен под рукой постоянно. */}
								<div className="flex h-full items-center border-l border-border-strong pl-1.5 ml-1">
									<NotificationButton
										unread={unreadNotifications}
										onClick={onToggleNotifications}
										size={11}
									/>
								</div>
							</>
						) : (
							<>
								<ThemeToggleButton
									resolvedTheme={resolvedTheme}
									onToggle={onToggleTheme}
									className="size-10"
								/>
								<LocaleSwitcher />
								<Button
									size="sm"
									className="cursor-pointer"
									onClick={() => {
										trackAuthCta("studyhub_header_login");
										useLoginModalStore.getState().open();
									}}
								>
									{t("topbar.login")}
								</Button>
							</>
						)}
					</div>
				</div>
			)}
		</header>
	);
});

const GUEST_NAV_LINK_CLASS = "px-3 py-1.5 rounded-md cursor-pointer transition-colors";

/**
 * Гостевое меню в шапке (desktop ≥ lg).
 *
 * Одни и те же пункты на ВСЕХ публичных страницах — раньше они были только на
 * главной, и на `/method`, `/about`, `/catalog`, `/pricing` шапка выглядела
 * пустой. Различается только способ перехода:
 *
 *   на главной   — нативный якорь `#section`, страница прокручивается сама
 *                  (`scroll-smooth` на лендинге), навигации роутера нет;
 *   вне главной  — переход роутером на `/` с якорем (SPA, без перезагрузки);
 *   «Метод»      — всегда `/method`, отдельная страница.
 *
 * Подсветка: у пункта-страницы — по адресу, у якорных — по scroll-spy. «Метод»
 * получает обе: на главной его подсвечивает одноимённая секция (`covers`), на
 * `/method` — совпадение пути.
 *
 * Mobile сюда не входит: в узкой шапке (бургер + логотип + тема + язык + «Войти»)
 * места нет, гостевые пункты живут в drawer'е (`GUEST_SIDEBAR_GROUPS`).
 */
function GuestNavLinks(): React.ReactElement {
	const { t } = useTranslation("layout");
	const pathname = useLocation({ select: (l) => l.pathname });
	const isHome = pathname === "/";
	// Вне главной секций в DOM нет — наблюдатель просто не создаётся и
	// `activeSection` остаётся null (см. useScrollSpy).
	const activeSection = useScrollSpy(SPY_SECTIONS);
	// Пункт «Тарифы» — якорь на секцию продажи. Скрыта секция — скрыт и пункт,
	// иначе меню ведёт в пустоту.
	const commerceVisible = useCommerceVisible();
	const navItems = commerceVisible
		? GUEST_NAV_ITEMS
		: GUEST_NAV_ITEMS.filter((item) => item.id !== "pricing");

	return (
		<nav
			aria-label={t("topbar.guestNav")}
			className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground"
		>
			{navItems.map((item) => {
				const isActive =
					activeSection === item.id || (item.to !== undefined && pathname === item.to);
				const className = cn(
					GUEST_NAV_LINK_CLASS,
					isActive
						? "text-foreground bg-muted/60 font-medium"
						: "hover:text-foreground hover:bg-muted/60",
				);
				const label = t(item.labelKey);

				if (item.to !== undefined) {
					return (
						<Link
							key={item.id}
							to={item.to}
							preload="intent"
							aria-current={isActive ? "page" : undefined}
							className={className}
						>
							{label}
						</Link>
					);
				}

				return isHome ? (
					<a
						key={item.id}
						href={`#${item.hash}`}
						aria-current={isActive ? "true" : undefined}
						className={className}
					>
						{label}
					</a>
				) : (
					<Link key={item.id} to="/" hash={item.hash} preload="intent" className={className}>
						{label}
					</Link>
				);
			})}
		</nav>
	);
}

// Кнопка-триггер репетитора — общий компонент TutorPanelTrigger (тот же, что и в
// шапках полноэкранных режимов). Здесь — тонкая обёртка ради единого call-site.
function TutorButton({ className, compact }: { className?: string; compact?: boolean }) {
	return <TutorPanelTrigger className={className} compact={compact} />;
}

function ThemeToggleButton({
	resolvedTheme,
	onToggle,
	className,
}: {
	resolvedTheme: "light" | "dark";
	onToggle: () => void;
	className?: string;
}) {
	const { t } = useTranslation("layout");
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={onToggle}
					className={cn(ICON_KEY_CLASS, "inline-flex items-center justify-center", className)}
					aria-label={resolvedTheme === "dark" ? t("topbar.themeLight") : t("topbar.themeDark")}
					aria-pressed={resolvedTheme === "dark"}
				>
					{resolvedTheme === "dark" ? (
						<IconSun size={18} stroke={1.75} />
					) : (
						<IconMoon size={18} stroke={1.75} />
					)}
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">{t("tooltips.theme")}</TooltipContent>
		</Tooltip>
	);
}

function NotificationButton({
	unread,
	onClick,
	size = 9,
}: {
	unread: number;
	onClick: () => void;
	size?: 9 | 11;
}) {
	const { t } = useTranslation("layout");
	if (size === 11) {
		// Mobile — крупная тач-кнопка через shadcn ghost-вариант.
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClick}
						className="relative size-10"
						aria-label={
							unread > 0
								? t("topbar.notificationsUnread", { count: unread })
								: t("topbar.notificationsAll")
						}
					>
						<IconBell size={18} stroke={1.75} />
						{unread > 0 && <UnreadBadge unread={unread} />}
					</Button>
				</TooltipTrigger>
				<TooltipContent side="bottom">{t("tooltips.notifications")}</TooltipContent>
			</Tooltip>
		);
	}
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={onClick}
					className={cn(ICON_KEY_CLASS, "relative inline-flex items-center justify-center")}
					aria-label={
						unread > 0
							? t("topbar.notificationsUnread", { count: unread })
							: t("topbar.notificationsAll")
					}
				>
					<IconBell size={18} stroke={1.75} />
					{unread > 0 && <UnreadBadge unread={unread} />}
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">{t("tooltips.notifications")}</TooltipContent>
		</Tooltip>
	);
}

function UnreadBadge({ unread }: { unread: number }) {
	return (
		<span
			className={cn(
				"absolute top-1 right-1 inline-flex items-center justify-center tabular-nums",
				"min-w-4 h-4 px-1 rounded-full bg-destructive text-white",
				"text-[10px] font-semibold leading-none ring-2 ring-background",
			)}
		>
			{unread > 99 ? "99+" : unread}
		</span>
	);
}

// Аватар-обёртка: ProfileDropdown с приподнятым тактильным аватаром-триггером.
function TopbarAvatar({ size }: { size: 8 | 11 }) {
	return (
		<ProfileDropdown
			size={size}
			triggerClassName={cn(
				// ml-2.5 — оптическая компенсация: залитый аватар без воздуха по краям
				// должен стоять дальше от колокола, чтобы зазор сравнялся с остальными.
				"ml-2.5 cursor-pointer rounded-full transition-transform duration-instant active:translate-y-px",
				"shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_2px_5px_rgb(var(--shadow)/0.25)]",
			)}
		/>
	);
}
